import React, { useState, useEffect } from "react";
import ApiService from "../Services/ApiServices";
import "./ProductReviewForm.css";

const ratingDescriptions = ["Poor", "Fair", "Good", "Very Good", "Excellent"];
const ratingIcons = ["😞", "😐", "😊", "😄", "🤩"];

const ProductReviewForm = ({
  item,
  initialRating = 0,
  onUpdateReview,
  onClose,
}) => {
  const [review, setReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);


  useEffect(() => {
    if (item?.deviceId) {
      fetchReviews();
    }
  }, [item]);

  useEffect(() => {
    setCharacterCount(description.length);
  }, [description]);

  // ✅ FETCH EXISTING REVIEW
  const fetchReviews = async () => {
    try {
      const response = await ApiService.post("/review/getWebReviewDetails");

      if (response.status) {
        const allReviews = response.data || [];

        const existingReview = allReviews.find(
          (r) =>
            r.deviceId === item.deviceId
          //  &&
            // r.clientId === clientDbId
        );

        if (existingReview) {
          setReview(existingReview);
          setRating(existingReview.rating || 0);
          setDescription(existingReview.review || "");
          setTitle(existingReview.reviewBy || ""); // mapping title safely
        } else {
          resetForm();
        }
      }
    } catch (error) {
      console.error("Reviews fetching failed:", error);
    }
  };

  const resetForm = () => {
    setReview(null);
    setRating(initialRating);
    setDescription("");
    setTitle("");
  };

  // ✅ SAVE / UPDATE REVIEW
  const handleUpdateReview = async (updatedReview) => {
    setIsSubmitting(true);

    try {
      const payload = {
        id: updatedReview.id || undefined,
        // clientId: clientDbId,
        deviceId: item.device.id,
        orderId: item.orderId || null,
        rating: updatedReview.rating,
        review: updatedReview.description,
        reviewBy: updatedReview.title, // mapped
      };

      const response = await ApiService.post(
        "/review/handleWebReviewDetails",
        payload
      );

      if (response.status) {
        onUpdateReview && onUpdateReview(payload);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Update review error:", error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ SUBMIT
  const handleSubmit = async () => {
    if (rating === 0) {
      alert("Please select a rating before submitting.");
      return;
    }

    if (description.trim() === "") {
      alert("Please provide a review description.");
      return;
    }

    if (description.length < 10) {
      alert("Please write at least 10 characters.");
      return;
    }

    const reviewData = {
      rating,
      title: title.trim() || `Review for ${item.name}`,
      description: description.trim(),
      id: review?.id,
    };

    const success = await handleUpdateReview(reviewData);

    if (success) {
      alert(
        review
          ? "Review updated successfully!"
          : "Review submitted successfully!"
      );
      onClose && onClose();
    } else {
      alert("Failed to submit review.");
    }
  };

  const handleClearForm = () => {
    resetForm();
    setHover(0);
  };

  const isFormValid = rating > 0 && description.trim().length >= 10;

  return (
    <div className="ProductReviewForm">
      <div className="review-header">
        <h2>{review ? "Update Review" : "Write a Review"}</h2>
        <p>Share your experience with {item?.name}</p>
      </div>

      {/* ⭐ Rating Section */}
      <div className="rating-section">
        <h3>How would you rate this product?*</h3>

        <div className="stars-container">
          <div className="stars-wrapper">
            {[1, 2, 3, 4, 5].map((star) => (
              <div key={star} className="star-item">
                <button
                  className={`star-button ${star <= (hover || rating) ? "filled" : ""
                    }`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  type="button"
                >
                  ★
                </button>

                {hover === star && (
                  <div className="rating-tooltip">
                    <span>{ratingIcons[star - 1]}</span>
                    <span>{ratingDescriptions[star - 1]}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {rating > 0 && (
            <div className="rating-display">
              <span>{rating}.0</span>
              <span>{ratingDescriptions[rating - 1]}</span>
            </div>
          )}
        </div>
      </div>

      {/* 📝 Form */}
      <div className="review-form-section">
        <div className="product-review-form-group">
          <label>Review Title (Optional)</label>
          <input
            type="text"
            placeholder="Summarize your experience..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
          <div className="character-count">{title.length}/100</div>
        </div>

        <div className="product-review-form-group">
          <label>Your Review*</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            maxLength={1000}
          />

          <div className="textarea-footer">
            <div>{characterCount}/1000</div>
            {characterCount < 10 && (
              <span className="char-warning">
                Minimum 10 characters required
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 🎯 Buttons */}
      <div className="product-review-action-buttons">
        <button onClick={handleClearForm} disabled={isSubmitting}>
          Clear
        </button>

        {onClose && (
          <button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
        )}

        <button
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          className="btn-primary"
        >
          {isSubmitting
            ? review
              ? "Updating..."
              : "Submitting..."
            : review
              ? "Update Review"
              : "Submit Review"}
        </button>
      </div>

      {/* 📘 Guidelines */}
      <div className="review-guidelines">
        <h4>Review Guidelines</h4>
        <ul>
          <li>Be specific about your experience</li>
          <li>Focus on product performance</li>
          <li>Avoid offensive language</li>
          <li>Help other customers</li>
        </ul>
      </div>
    </div>
  );
};

export default ProductReviewForm;